/**
 * The request grid behind the Templates and People drill-downs, for one period.
 *
 * Read only when a drawer opens (`query` is null until then), once per period:
 * both drawers on both tabs read the same grid, so flicking between rows costs
 * nothing. Refresh (`nonce`) starts a fresh cache, like the tables beside it.
 * A reply for a period no longer asked for is dropped.
 */
import { useEffect, useRef, useState } from 'react'
import { billingApi } from '../../services/api'
import { breakdownKey } from '../../utils/requestBreakdown'

export function useRequestBreakdown(query, nonce = 0) {
  const key = breakdownKey(query)
  const cacheKey = key ? `${nonce}|${key}` : null
  const cache = useRef(new Map())
  const [state, setState] = useState({ data: null, loading: false, error: null, key: null })

  useEffect(() => {
    if (!cacheKey) return undefined
    const hit = cache.current.get(cacheKey)
    if (hit) {
      setState({ data: hit, loading: false, error: null, key: cacheKey })
      return undefined
    }
    let cancelled = false
    setState({ data: null, loading: true, error: null, key: cacheKey })
    billingApi.getRequestBreakdown(query.range, query.custom)
      .then(res => {
        if (cancelled) return
        cache.current.set(cacheKey, res.data)
        setState({ data: res.data, loading: false, error: null, key: cacheKey })
      })
      .catch(err => {
        if (!cancelled) setState({ data: null, loading: false, error: err, key: cacheKey })
      })
    return () => { cancelled = true }
    // `query` is fully described by `cacheKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey])

  // Never hand back a grid for a different period than the one asked for.
  const current = state.key === cacheKey
  return {
    data: current ? state.data : null,
    loading: Boolean(cacheKey) && (!current || state.loading),
    error: current ? state.error : null,
  }
}
